import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLeftSidebar } from '@/hooks/useLeftSidebar';
import { api } from '@/lib/trpc/react';
import { cn } from '@/utils/utils';
import { useRouter } from 'next/router';
import { type ReactNode, useEffect } from 'react';

import { useWorkspace } from '@/hooks/useWorkspace';
const _Lottie = dynamic(() => import('lottie-react'), { ssr: false });
import ComModal from './ComModal';
import { EnableFunMode } from './EnableFunMode';
import Navbar from './Navbar';

import dynamic from 'next/dynamic';
import { LoadingSpinner } from './Loading';

export function SubLayout({
  children,
  bgColor,
  className,
}: {
  children: ReactNode;
  bgColor?: string;
  className?: string;
}) {
  return (
    <div
      className={`w-full h-full relative ${bgColor ? `bg-${bgColor}` : ''} ${className}`}
    >
      {children}
    </div>
  );
}

export const LoadingWrapper = ({ children }: { children: ReactNode }) => {
  const { isLoading } = useWorkspace();
  const { userLoading, user } = useAuth();
  if (!user || userLoading || isLoading)
    return (
      <div className="fixed inset-0 flex justify-center items-center z-50 bg-background">
        <div className="w-[56px] h-[56px]">
          <LoadingSpinner variant="screen" color="gray" />
        </div>
      </div>
    );
  return children;
};

export function AppLayout({ children }: { children: ReactNode }) {
  const router = useRouter();
  const { user } = useAuth();
  const { showLeftSidebar, setShowLeftSidebar } = useLeftSidebar();
  const { mutate: topCategoriesPosthog } =
    api.related.entityCategoriesPosthog.useMutation();

  useEffect(() => {
    if (user) {
      const lastExecutionTime = localStorage.getItem(
        'lastTopCategoriesPosthogTime',
      );
      const currentTime = Date.now();
      const twelveHoursInMs = 12 * 60 * 60 * 1000;

      if (
        !lastExecutionTime ||
        currentTime - Number.parseInt(lastExecutionTime) > twelveHoursInMs
      ) {
        topCategoriesPosthog();
        localStorage.setItem(
          'lastTopCategoriesPosthogTime',
          currentTime.toString(),
        );
      }
    }
  }, [user, topCategoriesPosthog]);

  const isMobile = useIsMobile();
  const isChatbotView =
    router.pathname.startsWith('/b') ||
    router.pathname.startsWith('/share') ||
    router.pathname.startsWith('/s');

  return (
    <LoadingWrapper>
      <div
        className={
          'flex flex-col items-between relative w-full h-full min-w-0 transition-all duration-200 ease-in-out'
        }
      >
        {isMobile && (
          <div
            className={cn(
              'fixed inset-0 bg-[#0C0C0C]/80 z-[29]  transition-opacity duration-200',
              showLeftSidebar ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
            onClick={() => setShowLeftSidebar(false)}
          />
        )}
        {!isChatbotView && <Navbar />}

        <EnableFunMode />
        <ComModal />
        <div
          className={
            'flex flex-col h-full min-h-0 transition-all duration-200 ease-in-out w-full '
          }
        >
          {children}
        </div>
      </div>
    </LoadingWrapper>
  );
}
